import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { PictureAsPdf as PdfIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const pad = (value) => String(value).padStart(2, "0");

const formatStamp = (date) =>
  `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(
    date.getHours()
  )}${pad(date.getMinutes())}`;

const formatDisplayDateTime = (date) =>
  `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;

const userDisplayName = (user, isRTL) => {
  const profile = user?.profile;
  if (profile) {
    if (isRTL) {
      return profile.arabic_name || profile.english_name || user.username || "";
    }
    return profile.english_name || profile.arabic_name || user.username || "";
  }
  return user?.username || user?.email || "";
};

const captureNode = async (node) => {
  const canvas = await html2canvas(node, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    windowWidth: Math.max(node.scrollWidth, node.clientWidth),
  });
  return canvas.toDataURL("image/png");
};

const addFittedImage = (pdf, imgData, pageWidth, pageHeight) => {
  const margin = 8;
  const footerSpace = 10;
  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin - footerSpace;
  const image = pdf.getImageProperties(imgData);
  const ratio = Math.min(
    usableWidth / image.width,
    usableHeight / image.height
  );
  const drawWidth = image.width * ratio;
  const drawHeight = image.height * ratio;
  const x = (pageWidth - drawWidth) / 2;
  pdf.addImage(imgData, "PNG", x, margin, drawWidth, drawHeight);
};

export const ReportPdfHeader = ({ visible, title, filtersSummary = [] }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const user = useSelector((state) => state.auth.user);

  if (!visible) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "background.paper",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        {title || t("reports.title")}
      </Typography>
      <Typography variant="body2">
        {t("reports.pdfExportedAt")}: {formatDisplayDateTime(new Date())}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t("reports.pdfUser")}: {userDisplayName(user, isRTL)}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {t("reports.pdfFilters")}
      </Typography>
      {filtersSummary.length === 0 ? (
        <Typography variant="body2">{t("reports.pdfNoFilters")}</Typography>
      ) : (
        filtersSummary.map((line) => (
          <Typography key={`${line.label}-${line.value}`} variant="body2">
            {line.label} : {line.value}
          </Typography>
        ))
      )}
    </Box>
  );
};

const ReportPdfExport = ({
  targetRef,
  pageRefs,
  reportType = "dashboard",
  onBeforeCapture,
  onAfterCapture,
}) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState("");

  const handleExport = async () => {
    const nodes = (pageRefs || [])
      .map((ref) => ref?.current)
      .filter(Boolean);
    if (!nodes.length && targetRef?.current) {
      nodes.push(targetRef.current);
    }
    if (!nodes.length) {
      setError(t("reports.pdfFailed"));
      return;
    }
    setIsExporting(true);
    setError("");
    try {
      if (onBeforeCapture) {
        await onBeforeCapture();
      }
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      await new Promise((resolve) => setTimeout(resolve, 80));

      const images = [];
      for (const node of nodes) {
        images.push(await captureNode(node));
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageCount = images.length;

      images.forEach((imgData, index) => {
        if (index > 0) {
          pdf.addPage();
        }
        addFittedImage(pdf, imgData, pageWidth, pageHeight);
      });

      for (let page = 1; page <= pageCount; page += 1) {
        pdf.setPage(page);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(80);
        pdf.text(`${page}-${pageCount}`, pageWidth / 2, pageHeight - 6, {
          align: "center",
        });
      }

      pdf.save(`report_${reportType}_${formatStamp(new Date())}.pdf`);
    } catch (err) {
      setError(err?.message || t("reports.pdfFailed"));
    } finally {
      if (onAfterCapture) {
        await onAfterCapture();
      }
      setIsExporting(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleExport}
        disabled={isExporting}
        startIcon={
          isExporting ? <CircularProgress size={18} color="inherit" /> : <PdfIcon />
        }
      >
        {isExporting ? t("reports.exportingPdf") : t("reports.exportPdf")}
      </Button>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
      >
        <Alert severity="error" onClose={() => setError("")} variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReportPdfExport;
